import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../Components/Layout/Layout';
import DishesList from '../Components/Dishes/DishesList';
import { Button } from "@/Components/ui/button";
import { Plus } from "lucide-react";
import axiosInstance from '../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';
import { DragDropContext } from '@hello-pangea/dnd';

const Dishes = () => {
    const { t } = useTranslation();
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [currency, setCurrency] = useState('€'); 

    const fetchDishes = async () => {
        try {
            setIsLoading(true); 
            
           const responseCurrency = await axiosInstance.get('/menu/GetQrSettings'); 
           const dataCurrency = await responseCurrency.data.currency;
           setCurrency(dataCurrency); 

            const response = await axiosInstance.get('/dish/GetDishes');
            const data = await response.data; 

            if (Array.isArray(data)) {
                setCategories(data);
            } else if (data.categories && Array.isArray(data.categories)) {
                setCategories(data.categories);
            } else {
                console.error('Unexpected API response format:', data);
                setCategories([]);
            }
        } catch (error) {
            console.error('Error fetching dishes:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDishes();
    }, []);

    const handleDragEnd = async (result) => {
        if (!result.destination) return;

        const { source, destination } = result;

        // Only allow reordering within the same category
        if (source.droppableId !== destination.droppableId) {
            return;
        }

        const categoryId = parseInt(source.droppableId);
        const updatedCategories = [...categories];
        const categoryIndex = updatedCategories.findIndex(cat => cat.id === categoryId);

        if (categoryIndex === -1) return;

        const category = updatedCategories[categoryIndex];
        const newDishes = Array.from(category.dishes);
        const [movedDish] = newDishes.splice(source.index, 1);
        newDishes.splice(destination.index, 0, movedDish);

        updatedCategories[categoryIndex] = {
            ...category,
            dishes: newDishes
        };

        // Update local state immediately for smooth UX
        setCategories(updatedCategories);

        try {
            // Prepare the order data
            const orderData = newDishes.map((dish, index) => ({
                id: dish.id,
                order: index + 1,
                categoryid: categoryId
            }));

            // Send the new order to the backend
            await axiosInstance.post('/dish/UpdateOrder', orderData);
        } catch (error) {
            console.error('Error updating dish order:', error);
            // Revert to original order if the API call fails
            fetchDishes();
        }
    };

    const handleAddDish = () => {
        navigate('/dish', {
            state: {
                dishId: 0,
                returnPage: 'dishes',
                activeTab: 'dishes'
            }
        });
    };

    if (isLoading) return (
        <Layout>
            <div>{t('common.loading')}</div>
        </Layout>
    );
    
    if (error) return (
        <Layout>
            <div>{t('common.error')}: {error}</div>
        </Layout>
    );

    return (
        <Layout>
            <div className="relative">
                <h2 className="text-xl font-normal font-inter text-ps-blue-dark mb-8">
                    {t('dishes.title')}
                </h2>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <DishesList categories={categories} currency={currency} />
                </DragDropContext>
                <div className="flex justify-end pt-8">
                    <Button
                        onClick={handleAddDish}
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-ps-green hover:bg-ps-green/90 text-white px-4 py-2 shadow-lg rounded-full"
                    >
                        <Plus className="h-6 w-6" />
                        <span>{t('dishes.addDish')}</span>
                    </Button>
                </div>
            </div>
        </Layout>
    );
};

export default Dishes;